// Handing an ESP32 BLE WWH gateway (DeviceType 103) its mesh keys — the
// `MESHNET:` step of docs/20-ble-wwh-esp32-gateway.md §5.
//
// The gateway joins the location's existing Bluetooth Mesh network as one more
// CONTROLLER, exactly like a phone: the location's NetKey / AppKey / IV index,
// and a source address of ITS OWN from Location.MeshNextClient — never a
// phone's, or the lamps' replay protection drops half of everyone's commands
// (meshClientStore.js has the long version).
//
// ⚠️ OVER THE AUTHENTICATED BLE SESSION ONLY, never MQTT. And never logged:
// maskMeshNet() is what the trace prints instead.

import { getDeviceDetail } from '../../api/api';
import { getBleNodesAtLocation, getMeshNetwork } from '../../api/bleNodes';
import { isMeshCodec } from './meshValues';

export const GATEWAY_DEVICE_ID = 103;

const hex4 = (n) => Number(n).toString(16).toUpperCase().padStart(4, '0');
const HEX32 = /^[0-9a-f]{32}$/i;

/** network (GetMeshNetwork's reply) -> "MESHNET:<net>,<app>,<iv>,<addr>". Throws on bad keys. */
export function meshNetCommand(network) {
  const { NetKey, AppKey } = network || {};
  const iv = Number(network?.IvIndex) || 0;
  const addr = Number(network?.ClientAddress);
  if (!HEX32.test(String(NetKey)) || !HEX32.test(String(AppKey))) throw new Error('The server returned no valid mesh keys');
  if (!(addr >= 0x7000 && addr <= 0x7fff)) throw new Error('The server returned no controller address');
  return `MESHNET:${String(NetKey).toLowerCase()},${String(AppKey).toLowerCase()},${iv},${hex4(addr)}`;
}

/** What the trace may show of a MESHNET command: everything but the keys. */
export function maskMeshNet(text) {
  const m = /^MESHNET:([0-9a-f]{32}),([0-9a-f]{32}),(\d+),([0-9a-f]{4})$/i.exec(String(text || ''));
  return m ? `MESHNET:<net key>,<app key>,${m[3]},${m[4].toUpperCase()}` : text;
}

export const isMeshNetOk = (text) => /MESHNET\s+OK/i.test(String(text || ''));
export const isMeshNetRejected = (text) => /MESHNET\s+REJECTED/i.test(String(text || ''));

/**
 * The network for the gateway `uniqueNumber`: its location (from its device
 * row) -> any Bluetooth Mesh group/parent there (the server's access rule is
 * per node) -> GetMeshNetwork with NO client address, so the server allocates
 * a FRESH one for the gateway. -> { network, nodeName }
 */
export async function fetchGatewayNetwork(userId, uniqueNumber) {
  const res = await getDeviceDetail(uniqueNumber);
  const row = Array.isArray(res?.data) ? res.data[0] : res?.data;
  const locationId = Number(row?.LocationID ?? row?.LocationId);
  if (!locationId) throw new Error('This gateway has no location yet — assign it to a location first');
  const nodes = (await getBleNodesAtLocation(userId, locationId)).nodes || [];
  const mesh = nodes.find((n) => isMeshCodec(n.Codec));
  if (!mesh) throw new Error('No Bluetooth Mesh lamp group at this location yet — create one and pair a lamp first');
  const { network } = await getMeshNetwork(userId, mesh.NodeId, null);
  return { network, nodeName: mesh.Name };
}
